import * as THREE from "three";
import { useMemo } from "react";
import { useLoader } from "@react-three/fiber";
import { useRoomLabel } from "../../../lib/hooks/useRoomLabel";
import { useBox } from "@react-three/cannon";
import { MEZZANINE_Y } from "../../../lib/consts";
import GlassQuad from "../../room1/GlassQuad";
import RoomLight from "../../RoomLight";
import Table from "../table/Table";

const FLOOR_THICKNESS = 0.3;
const CEILING_THICKNESS = 2;
const ROOM_WIDTH = 5;
const ROOM_LENGTH = 7;
const CEILING_BOTTOM = MEZZANINE_Y + 4;
const WALL_HEIGHT = CEILING_BOTTOM - (MEZZANINE_Y + FLOOR_THICKNESS);
const WALL_CENTER_Y = MEZZANINE_Y + FLOOR_THICKNESS + WALL_HEIGHT / 2;
const GLASS_HEIGHT = CEILING_BOTTOM - MEZZANINE_Y;
const GLASS_CENTER_Y = MEZZANINE_Y + GLASS_HEIGHT / 2;


const CX = 12.5;
const CZ = 0;


export default function UpholsteryService() {
	const cx = CX, cz = CZ;
	const leftX = cx - ROOM_WIDTH / 2;
	const rightX = cx + ROOM_WIDTH / 2;
	const backZ = cz - ROOM_LENGTH / 2;
	const frontZ = cz + ROOM_LENGTH / 2;

	const ceilingTexture = useLoader(THREE.TextureLoader, "/tran/go.png");
	const tex = useMemo(() => {
		const t = ceilingTexture.clone();
		t.wrapS = t.wrapT = THREE.RepeatWrapping;
		t.repeat.set(1, 1);
		t.anisotropy = 4;
		return t;
	}, [ceilingTexture]);

	const labelTexture = useRoomLabel("Upholstery Service");


	const [floorRef] = useBox(() => ({
		type: "Static", args: [ROOM_WIDTH, FLOOR_THICKNESS, ROOM_LENGTH],
		position: [cx, MEZZANINE_Y + FLOOR_THICKNESS / 2, cz],
	}));
	const [leftWallRef] = useBox(() => ({
		type: "Static", args: [0.05, WALL_HEIGHT, ROOM_LENGTH],
		position: [leftX, WALL_CENTER_Y, cz],
	}));
	const [rightWallRef] = useBox(() => ({
		type: "Static", args: [0.05, WALL_HEIGHT, ROOM_LENGTH],
		position: [rightX, WALL_CENTER_Y, cz],
	}));
	const [backWallRef] = useBox(() => ({
		type: "Static", args: [ROOM_WIDTH, WALL_HEIGHT, 0.05],
		position: [cx, WALL_CENTER_Y, backZ],
	}));
	const [frontWallRef] = useBox(() => ({
		type: "Static", args: [ROOM_WIDTH, WALL_HEIGHT, 0.05],
		position: [cx, WALL_CENTER_Y, frontZ],
	}));

	return (
		<group>
			<mesh ref={floorRef} position={[cx, MEZZANINE_Y + FLOOR_THICKNESS / 2, cz]} receiveShadow visible={false}>
				<boxGeometry args={[ROOM_WIDTH, FLOOR_THICKNESS, ROOM_LENGTH]} />
				<meshStandardMaterial color="#c3b091" roughness={0.7} />
			</mesh>
			<mesh position={[cx, CEILING_BOTTOM + CEILING_THICKNESS / 2, cz]} castShadow receiveShadow>
				<boxGeometry args={[ROOM_WIDTH, CEILING_THICKNESS, ROOM_LENGTH]} />
				<meshStandardMaterial map={tex} roughness={0.7} side={THREE.DoubleSide} />
			</mesh>
			<RoomLight position={[cx, CEILING_BOTTOM, cz]} />
			{labelTexture && (
				<mesh position={[leftX - 0.02, CEILING_BOTTOM + CEILING_THICKNESS / 2 - 0.28, cz]} rotation={[0, -Math.PI / 2, 0]}>
					<planeGeometry args={[7, 1.2]} />
					<meshStandardMaterial map={labelTexture} transparent />
				</mesh>
			)}
			<mesh ref={leftWallRef} visible={false} />
			<mesh ref={rightWallRef} visible={false} />
			<mesh ref={backWallRef} visible={false} />
			<mesh ref={frontWallRef} visible={false} />
			<GlassQuad width={ROOM_LENGTH} height={GLASS_HEIGHT} position={[leftX, GLASS_CENTER_Y, cz]} rotation={([0, Math.PI / 2, 0] as [number, number, number])} hide={{ left: true, right: true }} />
			<GlassQuad width={ROOM_WIDTH} height={GLASS_HEIGHT} position={[cx, GLASS_CENTER_Y, backZ]} hide={{ left: true, right: true }} />
			<GlassQuad width={ROOM_WIDTH} height={GLASS_HEIGHT} position={[cx, GLASS_CENTER_Y, frontZ]} rotation={[0, Math.PI, 0]} hide={{ left: true, right: true }} />
			<Table position={[cx, MEZZANINE_Y, cz]} />
		</group>
	);
}